import {api} from "@/shared/api";
import * as Input from "@/shared/utils/input";
import {Combobox} from "@/shared/ui/combobox";
import {SecurityStatus} from "@/shared/securityStatus";
import {WorkingTimeRequestStepConfigurationType} from "@/shared/timrTypes";
import {PartialUser, WorkingTimeRequestStepConfiguration} from "@/shared/timr";

const template = `
		<div class="flex-row mb-10">
			<div class="flex-column-60">
				<span class="text-nowrap">${messages["workingTimeRequestStepsConfigurationComponent.approvalBy"]}</span>
			</div>
			<div class="flex-column-40 text-center">
				<span class="text-nowrap">${messages["workingTimeRequestStepsConfigurationComponent.cancellationApprovalRequired"]}</span>
			</div>
		</div>
		<div class="configuration-container" data-field="workingTimeRequestStepsConfiguration">
		</div>
	`;

export class WorkingTimeRequestStepsConfigurationComponent extends HTMLElement {

	public static readonly EVENT_CHANGED: string = "workingTimeRequestStepsConfigurationComponent.changed";

	private static readonly MIN_STEPS: number = 1;
	private static readonly MAX_STEPS: number = 4;

	private _config?: WorkingTimeRequestStepConfiguration[];
	private availableUsers: PartialUser[];
	private nrOfSteps: number;

	private findAllActiveUsersRequest: JQuery.jqXHR<PartialUser[]>;

	/**
	 * Sets the working time request step configuration and triggers a re-render of the component.
	 * @param config Array of `WorkingTimeRequestStepConfiguration` objects to set as the current configuration.
	 */
	public set config(config: WorkingTimeRequestStepConfiguration[]) {
		this._config = config;
		this.render();
	}

	/**
	 * Gets the current working time request step configuration from the DOM.
	 * @returns Array of `WorkingTimeRequestStepConfiguration` objects representing the current configuration.
	 */
	public get config(): WorkingTimeRequestStepConfiguration[] {
		const configuration: WorkingTimeRequestStepConfiguration[] = [];

		for (let i = 0; i < WorkingTimeRequestStepsConfigurationComponent.MAX_STEPS; i++) {
			const typeSelect: HTMLSelectElement = this.querySelector(`select[name="workingTimeRequestStepsConfiguration[${i}].type"]`);
			const approvalCheckbox: HTMLInputElement = this.querySelector(`input[name="workingTimeRequestStepsConfiguration[${i}].approvalForCancellationRequired"]`);
			const userIdInput: HTMLInputElement = this.querySelector(`input[name="workingTimeRequestStepsConfiguration[${i}].userId"]`);

			if (typeSelect?.value) {
				const stepConfig: WorkingTimeRequestStepConfiguration = {
					type: typeSelect.value as WorkingTimeRequestStepConfigurationType,
					approvalForCancellationRequired: approvalCheckbox?.checked || false
				};

				if (userIdInput?.value) {
					stepConfig.userId = parseInt(userIdInput.value, 10);
				}

				configuration.push(stepConfig);
			}
		}

		return configuration;
	}

	protected connectedCallback(): void {
		this.innerHTML = template;
		this.nrOfSteps = SecurityStatus.isWorkingTimeRequestsMultistepAllowed() ?
			WorkingTimeRequestStepsConfigurationComponent.MAX_STEPS :
			WorkingTimeRequestStepsConfigurationComponent.MIN_STEPS
		this.render();
		this.findAllActiveUsersRequest?.abort();
		this.findAllActiveUsersRequest = api("admin/findAllActiveUsers.json")
			.get()
			.done((users: PartialUser[]) => {
				this.availableUsers = users;
				this.render();
			});
	}

	private buildLoadingSkeleton(): void {
		const container = this.querySelector(".configuration-container");
		container.replaceChildren(); // Clear previous content
		for (let i = 0; i < WorkingTimeRequestStepsConfigurationComponent.MAX_STEPS; i++) {
			const row = this.createElementWithClasses("div", "flex-row");
			row.innerHTML = `
					<div class="configuration-select">
						<div class="loading-skeleton"></div>
					</div>
				`;
			container.appendChild(row);
		}
	}

	private render() {
		// Assure everything is loaded before building the configuration
		if (!this.availableUsers || !this._config) {
			this.buildLoadingSkeleton();
		} else {
			this.buildConfiguration();
		}
	}

	private buildConfiguration(): void {
		const container = this.querySelector(".configuration-container");
		container.replaceChildren(); // Clear previous content
		// Always build maximum steps
		for (let i = 0; i < WorkingTimeRequestStepsConfigurationComponent.MAX_STEPS; i++) {
			// build marketing preview steps if nrOfSteps < MAX_STEPS
			if (i < this.nrOfSteps) {
				container.appendChild(this.buildConfigurationStep(i));
			} else {
				container.appendChild(this.buildMarketingPreviewStep());
			}
		}
	}

	private buildConfigurationStep(i: number): Node {
		const row = this.createElementWithClasses("div", "flex-row", "form-group");

		const select = this.createElementWithClasses("select", "form-control") as HTMLSelectElement;
		select.name = `workingTimeRequestStepsConfiguration[${i}].type`;
		select.appendChild(this.createOption("", "---"));
		Object.keys(WorkingTimeRequestStepConfigurationType).forEach((v) => {
			if (this.isWorkingTimeRequestStepConfigurationTypeAllowed(v as WorkingTimeRequestStepConfigurationType)) {
				select.appendChild(this.createOption(v, messages[`workingTimeRequestStepConfigurationType.${v}`]));
			}
		});
		const type = this._config[i]?.type;
		select.value = this.isWorkingTimeRequestStepConfigurationTypeAllowed(type) ? type : "";
		select.addEventListener("change", () => this.onSelectChanged(i, select));

		const selectContainer = this.createElementWithClasses("div", "configuration-select");
		selectContainer.appendChild(select);

		if (this._config[i]?.userId) {
			selectContainer.appendChild(this.buildUserSelect(i, this._config[i].userId));
		}

		const checkbox = this.createInput("checkbox", "", "");
		checkbox.name = `workingTimeRequestStepsConfiguration[${i}].approvalForCancellationRequired`;
		checkbox.disabled = !select.value;
		checkbox.checked = this._config[i]?.approvalForCancellationRequired ?? false;
		checkbox.addEventListener("change", () => this.fireChangedEvent());

		const checkboxContainer = this.createElementWithClasses("div", "configuration-checkbox");
		checkboxContainer.appendChild(checkbox);

		row.append(selectContainer, checkboxContainer);
		return row;
	}

	private buildMarketingPreviewStep(): Node {
		const row = this.createElementWithClasses("div", "flex-row", "form-group");

		const select = this.createElementWithClasses("select", "form-control") as HTMLSelectElement;
		select.appendChild(this.createOption("", `${messages["workingTimeRequestStepConfigurationType.placeholder"]}`));
		select.disabled = true;

		const selectContainer = this.createElementWithClasses("div", "configuration-select");
		selectContainer.appendChild(select);

		const checkbox = this.createInput("checkbox", "", "");
		checkbox.disabled = true;

		const checkboxContainer = this.createElementWithClasses("div", "configuration-checkbox");
		checkboxContainer.appendChild(checkbox);

		row.append(selectContainer, checkboxContainer);
		return row;
	}

	private buildUserSelect(idx: number, userId?: number): HTMLElement {
		const inputGroup = this.createElementWithClasses("div", "input-group", "configuration-user", "expanded", "mt-10");
		inputGroup.setAttribute("data-user-combobox", "");

		const hiddenInput = this.createInput("hidden", "user.id", "data-user-combobox-id");
		hiddenInput.name = `workingTimeRequestStepsConfiguration[${idx}].userId`;
		const textInput = this.createInput("text", "", "data-user-combobox-filter", "form-control");
		const button = this.createButtonWithIcon("fas fa-caret-down", "-1", "data-user-combobox-caret");

		const span = this.createElementWithClasses("span", "input-group-btn");
		span.appendChild(button);

		inputGroup.append(hiddenInput, textInput, span);

		const combobox = new Combobox<PartialUser>({
			caretElement: $(span),
			filterElement: $(textInput),
			formatter: (item: PartialUser) => item.fullname,
			emptyMessage: messages["workingTimeRequestStepsConfigurationComponent.userNotFound"],
			idElement: $(hiddenInput),
			items: this.availableUsers
		});

		combobox.setSelectedItemById(userId, !userId);
		$(combobox).on(Combobox.EVENT_CHANGED, () => this.fireChangedEvent());
		return inputGroup;
	}

	private onSelectChanged(idx: number, select: HTMLSelectElement): void {
		const selectedValue = select.value as WorkingTimeRequestStepConfigurationType;
		const checkbox: HTMLInputElement = select.parentElement?.nextElementSibling?.querySelector("input[type='checkbox']");
		Input.toggleCheckbox(checkbox, !selectedValue, true);

		const configurationSelectContainer = select.closest(".configuration-select");
		if (selectedValue === WorkingTimeRequestStepConfigurationType.USER) {
			const userSelect = this.buildUserSelect(idx);
			select.parentElement?.appendChild(userSelect);
		} else {
			const userSelect = configurationSelectContainer?.querySelector("[data-user-combobox]");
			if (userSelect) {
				userSelect.remove();
			}
		}
		this.fireChangedEvent();
	}

	private createOption(value: string, text: string): HTMLOptionElement {
		const option = document.createElement("option");
		option.value = value;
		option.textContent = text;
		return option;
	}

	private createElementWithClasses(tag: string, ...classes: string[]): HTMLElement {
		const element = document.createElement(tag);
		element.classList.add(...classes);
		return element;
	}

	private createInput(type: string, name: string, dataAttribute: string, ...classes: string[]): HTMLInputElement {
		const input = document.createElement("input");
		input.type = type;
		if (name) input.name = name;
		if (dataAttribute) input.setAttribute(dataAttribute, "");
		input.classList.add(...classes);
		return input;
	}

	private createButtonWithIcon(iconClass: string, tabIndex: string, dataAttribute: string): HTMLButtonElement {
		const button = this.createElementWithClasses("button", "btn", "btn-default", "btn-icon") as HTMLButtonElement;
		button.type = "button";
		button.setAttribute("tabindex", tabIndex);
		if (dataAttribute) button.setAttribute(dataAttribute, "");
		const icon = this.createElementWithClasses("i", ...iconClass.split(" "));
		button.appendChild(icon);
		return button;
	}

	/**
	 * Checks if the given `WorkingTimeRequestStepConfigurationType` is allowed based on the current security status.
	 *
	 * Change the corresponding java method too if you change the allowed types.
	 * -> `WorkingTimeRequestStepConfigurationType.isWorkingTimeRequestStepConfigurationTypeAllowed()`
	 * @param type
	 * @private
	 */
	private isWorkingTimeRequestStepConfigurationTypeAllowed(type?: WorkingTimeRequestStepConfigurationType): boolean {
		if (!type) {
			return false;
		} else {
			switch (type) {
				case WorkingTimeRequestStepConfigurationType.SUBSTITUTE:
					return SecurityStatus.isSubstitutesAllowed();
				case WorkingTimeRequestStepConfigurationType.TEAM_LEADER_DIRECT_OR_HIGHER:
				case WorkingTimeRequestStepConfigurationType.TEAM_LEADER_OR_ADMINISTRATOR:
					return SecurityStatus.isCorporateFeaturesAllowed();
				case WorkingTimeRequestStepConfigurationType.USER:
				case WorkingTimeRequestStepConfigurationType.ADMINISTRATOR:
					return true;
			}
		}
	}

	private fireChangedEvent() {
		this.dispatchEvent(new CustomEvent(WorkingTimeRequestStepsConfigurationComponent.EVENT_CHANGED));
	}
}
